import type {
  Category,
  Choice,
  Config,
  Consent,
  ConsentChangeListener,
  Decision,
  PendingEvent,
  ReadyListener,
} from './types';
import {
  ALL_CATEGORIES,
  buildDecision,
  clearDecision,
  consentString,
  emptyConsent,
  fullConsent,
  writeDecision,
} from './storage';
import { pushDataLayerEvent, pushGcmDefault, pushGcmUpdate } from './gcm';
import { unblock } from './unblock';
import { renderBanner, removeBanner } from './banner';
import {
  clearPendingQueue,
  loadPendingQueue,
  postConsent,
  savePendingQueue,
} from './network';

export const SNIPPET_VERSION = '0.1.0';
export const UI_VERSION = 1;

type EventName = 'cookiesie:consent' | 'cookiesie:ready';

interface ApiState {
  projectId: string;
  config: Config;
  decision: Decision | null;
  ready: boolean;
  apiBase: string;
}

export interface CookiesieApi {
  init(opts?: { projectId?: string; configUrl?: string; locale?: 'en' | 'ga' }): void;
  getConsent(): Consent | null;
  hasDecision(): boolean;
  getCategory(c: Category): boolean;
  setConsent(c: Partial<Consent>): void;
  acceptAll(): void;
  rejectAll(): void;
  reopenPreferences(): void;
  withdrawConsent(): void;
  onConsentChange(cb: ConsentChangeListener): () => void;
  onReady(cb: ReadyListener): () => void;
  version: string;
  projectId: string;
}

export function createApi(state: ApiState): CookiesieApi {
  const consentListeners = new Set<ConsentChangeListener>();
  const readyListeners = new Set<ReadyListener>();

  function emit(name: EventName, detail?: Consent): void {
    const evt = detail
      ? new CustomEvent(name, { detail })
      : new Event(name);
    document.dispatchEvent(evt);
  }

  function fireConsent(c: Consent): void {
    consentListeners.forEach((cb) => {
      try {
        cb(c);
      } catch {}
    });
    emit('cookiesie:consent', c);
  }

  function fireReady(): void {
    state.ready = true;
    readyListeners.forEach((cb) => {
      try {
        cb();
      } catch {}
    });
    emit('cookiesie:ready');
  }

  function applyConsent(c: Consent, choice: Choice): void {
    const decision = buildDecision(c, state.config.policyRevision, choice);
    state.decision = decision;
    writeDecision(decision);
    pushGcmUpdate(c);
    unblock(c);
    pushDataLayerEvent(c);
    sendConsent(state, decision);
    fireConsent(c);
    removeBanner();
    bindFooterTriggers();
  }

  function reopen(): void {
    renderBanner(
      state.config,
      {
        onAcceptAll: () => applyConsent(fullConsent(), 'accept_all'),
        onRejectAll: () => applyConsent(emptyConsent(), 'reject_all'),
        onSavePartial: (c) => applyConsent(c, 'custom'),
      },
      state.decision?.c ?? null,
      true,
    );
  }

  function bindFooterTriggers(): void {
    document
      .querySelectorAll<HTMLAnchorElement>('a[href="#cookies-prefs"]')
      .forEach((a) => {
        if (a.hasAttribute('data-cookiesie-bound')) return;
        a.setAttribute('data-cookiesie-bound', '1');
        a.addEventListener('click', (e) => {
          e.preventDefault();
          reopen();
        });
      });
  }

  const api: CookiesieApi = {
    init() {
      // No-op: bootstrap already initialised; provided so test harnesses can
      // override projectId/configUrl/locale before they ship in Phase 2.
    },
    getConsent: () => (state.decision ? { ...state.decision.c } : null),
    hasDecision: () => !!state.decision,
    // Whitelist before the property read so `getCategory('__proto__')`,
    // `'constructor'`, `'toString'`, etc. don't return truthy via the
    // consent object's prototype chain. Same defence shape as
    // `categoryAllowed` in unblock.ts; window.cookiesie is reachable to
    // any code on the page, including TM/CMS rules where `cat` is a string
    // field that can drift to a prototype key name.
    getCategory: (c) =>
      ALL_CATEGORIES.includes(c) && !!state.decision?.c[c],
    setConsent: (partial) => {
      const next: Consent = { ...(state.decision?.c ?? emptyConsent()) };
      for (const k of ALL_CATEGORIES) {
        if (k in partial) next[k] = !!partial[k];
      }
      next.strictly_necessary = true;
      applyConsent(next, 'custom');
    },
    acceptAll: () => applyConsent(fullConsent(), 'accept_all'),
    rejectAll: () => applyConsent(emptyConsent(), 'reject_all'),
    reopenPreferences: () => reopen(),
    withdrawConsent: () => {
      clearDecision();
      state.decision = null;
      pushGcmUpdate(emptyConsent());
      reopen();
    },
    onConsentChange: (cb) => {
      consentListeners.add(cb);
      return () => consentListeners.delete(cb);
    },
    onReady: (cb) => {
      if (state.ready) {
        try {
          cb();
        } catch {}
      } else {
        readyListeners.add(cb);
      }
      return () => readyListeners.delete(cb);
    },
    version: SNIPPET_VERSION,
    projectId: state.projectId,
  };

  // Internal: surface for bootstrap to fire ready + bind footer triggers
  // without exposing them on the public API surface.
  (api as any)._fireReady = fireReady;
  (api as any)._bindFooter = bindFooterTriggers;
  (api as any)._renderBanner = (startInPrefs = false) =>
    renderBanner(
      state.config,
      {
        onAcceptAll: () => applyConsent(fullConsent(), 'accept_all'),
        onRejectAll: () => applyConsent(emptyConsent(), 'reject_all'),
        onSavePartial: (c) => applyConsent(c, 'custom'),
      },
      state.decision?.c ?? null,
      startInPrefs,
    );

  return api;
}

function buildPendingEvent(state: ApiState, decision: Decision): PendingEvent {
  return {
    projectId: state.projectId,
    choice: decision.choice,
    categories: decision.c,
    consentString: consentString(decision.c),
    uiVersion: UI_VERSION,
    snippetVersion: SNIPPET_VERSION,
    language: state.config.language,
    policyRevision: state.config.policyRevision,
    bannerTextHash: state.config.bannerTextHash ?? '',
    pageUrl: location.origin + location.pathname,
    ts: Date.now(),
  };
}

// Primary path: POST live with keepalive. Falls back to a localStorage
// retry queue (capped at 5 events per device, mvp_b_plan.md §5.3) which
// drainPending() flushes on next page load. Terminal 4xxs (422, 404, 403,
// 429 quota_exceeded) are dropped — they would never succeed on retry.
function sendConsent(state: ApiState, decision: Decision): void {
  if (!state.projectId) return;
  const evt = buildPendingEvent(state, decision);
  // Pre-fetch path: the live config (and thus bannerTextHash) hasn't landed.
  // Queue instead of dropping — drainPending() will stamp the live hash and
  // post it once the config arrives. Earlier this was a silent drop, which
  // lost any consent decided before the post-paint config fetch resolved.
  if (!state.config.bannerTextHash) {
    const queue = loadPendingQueue();
    queue.push(evt);
    savePendingQueue(queue);
    return;
  }
  postConsent(`${state.apiBase}/consent`, evt).then((outcome) => {
    if (outcome.kind !== 'retry') return;
    const queue = loadPendingQueue();
    queue.push(evt);
    savePendingQueue(queue);
  });
}

// Stable identity for a queued event. Date.now() at queue time is unique
// per device in practice — even rapid programmatic toggling can't collide
// at sub-ms precision, and the additional fields make collisions inert.
function eventKey(e: PendingEvent): string {
  return `${e.projectId}|${e.ts}|${e.choice}`;
}

export async function drainPending(state: ApiState): Promise<void> {
  if (!state.projectId || !state.config.bannerTextHash) return;
  const initialQueue = loadPendingQueue();
  if (initialQueue.length === 0) return;

  // Per-event outcome: drained (remove from queue) or restamped (kept with
  // live banner_text_hash). Tracked by key so we can do a read-modify-write
  // at the end and not clobber events queued by sendConsent during our
  // awaits — the earlier wholesale `savePendingQueue(stillPending)` did
  // exactly that, silently dropping any user choice made mid-drain.
  const drained = new Set<string>();
  const restamped = new Map<string, PendingEvent>();

  for (const evt of initialQueue) {
    // Pre-fetch events may have been queued with an empty bannerTextHash /
    // policyRevision 1 (FALLBACK). Stamp with the live values so the server
    // accepts them — the user's choice itself stands.
    const stamped: PendingEvent = {
      ...evt,
      bannerTextHash: evt.bannerTextHash || state.config.bannerTextHash,
      policyRevision: evt.bannerTextHash ? evt.policyRevision : state.config.policyRevision,
    };
    const outcome = await postConsent(`${state.apiBase}/consent`, stamped);
    const key = eventKey(evt);
    if (outcome.kind === 'retry') {
      restamped.set(key, stamped);
    } else {
      // 'ok' and 'drop' both remove from the queue.
      drained.add(key);
    }
  }

  // Read-modify-write: drop drained events, replace retried events with
  // their stamped form, leave anything else (events appended by
  // sendConsent during our awaits) untouched.
  const current = loadPendingQueue();
  const next: PendingEvent[] = [];
  for (const evt of current) {
    const key = eventKey(evt);
    if (drained.has(key)) continue;
    if (restamped.has(key)) {
      next.push(restamped.get(key)!);
      continue;
    }
    next.push(evt);
  }
  if (next.length === 0) clearPendingQueue();
  else savePendingQueue(next);
}

export { pushGcmDefault };
